











































import {Component, Emit, Prop} from "vue-property-decorator";
import ReportCreate from "@/models/report/ReportCreate";
import {AxiosError} from "axios";
import AsiDialogFormLayout from "@/components/common/AsiDialogFormLayout.vue";
import AsiDialogForm from "@/components/common/AsiDialogForm.vue";
import OrderSimple from "@/models/order/OrderSimple";
import OrderAutocompleteInput from "@/components/order/OrderAutocompleteInput.vue";
import UserSimple from "@/models/user/UserSimple";
import UserAutocompleteInput from "@/components/user/UserAutocompleteInput.vue";
import AsiDatePicker from "@/components/common/AsiDatePicker.vue";
import AsiTextField from "@/components/common/AsiTextField";
import AsiTimePicker from "@/components/common/AsiTimePicker.vue";
import AsiTextarea from "@/components/common/AsiTextarea";
import {toISODateTimeString} from "@/helpers/functions_asi";
import AsiSelect from "@/components/common/AsiSelect";
import AsiFadeTransition from "@/components/common/AsiFadeTransition.vue";
import UserListFilter from "@/models/user/UserListFilter";
import {CompanyType, Permissions} from "@/helpers/constants";
import OrderListFilter from "@/models/order/OrderListFilter";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import Order from "@/models/order/Order";
import OrderListEntry from "@/models/order/OrderListEntry";
import {ValidationRule} from "@/declarations/ValidationRule";
import AsiAlert from "@/components/common/AsiAlert.vue";
import CompanySimple from "@/models/company/CompanySimple";

@Component({
	components: {
		AsiAlert,
		AsiFadeTransition,
		AsiSelect,
		AsiTextarea,
		AsiTimePicker,
		AsiTextField, AsiDatePicker, UserAutocompleteInput, OrderAutocompleteInput, AsiDialogFormLayout
	}
})
export default class ReportCreateDialog extends AsiDialogForm {

	@Prop({type: Object, default: null})
	public company!: CompanySimple | null;

	@Prop({type: Object, default: null})
	public order!: OrderSimple | null;

	@Prop({type: Object, default: null})
	public user!: UserSimple | null;

	@Prop({type: String, default: null})
	public date!: string;

	@Prop({type: String, default: null})
	public planningEntryId!: string;

	@Prop({type: Boolean, default: false})
	public noRedirectOnSave!: boolean;

	protected model: ReportCreate = new ReportCreate();
	private fromTimeInternal: string | null = null;
	private toTimeInternal: string | null = null;
	private minDate: string | null = null;
	private maxDate: string | null = null;
	private breakDurationRules: ValidationRule[] = [];

	private get isOffice(): boolean {
		return this.$store.getters['user/hasPermission'](Permissions.rightOffice);
	}

	private get breakOptions(): { text: string; value: number }[] {
		const ret: { text: string; value: number }[] = [
			{
				text: this.$t('uiTerms.noBreak').toString(),
				value: 0,
			}
		];

		let cur = 5;
		let curHours: number;
		let curMinutes: number;
		while (cur <= 6 * 60) {
			curMinutes = cur % 60;
			curHours = Math.floor(cur / 60);
			ret.push({
				text: curHours + ':' + curMinutes.toString().padStart(2, '0'),
				value: cur,
			})
			cur += 5;
		}

		return ret;
	}

	private get currentDurationMinutes(): number | null {
		if (this.model.fromTime === null || this.model.toTime === null) {
			return null;
		}

		const dFrom = new Date(this.model.fromTime);
		const dTo = new Date(this.model.toTime);
		return (dTo.getTime() - dFrom.getTime()) / 60000;
	}

	private get fromTime(): string | null {
		return this.fromTimeInternal;
	}

	private set fromTime(value: string | null) {
		this.fromTimeInternal = value;
		this.recalculateTimeFields();
		this.createBreakDurationRules();
	}

	private get toTime(): string | null {
		return this.toTimeInternal;
	}

	private set toTime(value: string | null) {
		this.toTimeInternal = value;
		this.recalculateTimeFields();
		this.createBreakDurationRules();
	}

	private get isOverMidnight(): boolean {
		if (this.fromTimeInternal === null || this.toTimeInternal === null) {
			return false;
		}

		return this.fromTimeInternal.localeCompare(this.toTimeInternal) > 0;
	}

	private get orderListFilter(): OrderListFilter {
		const filter = new OrderListFilter();
		if (this.model.date !== null) {
			filter.dateFrom = this.model.date;
			filter.dateTo = this.model.date;
		}
		return filter;
	}

	private get userListFilter(): UserListFilter {
		const filter = new UserListFilter();
		filter.companyTypes = [CompanyType.internal];
		return filter;
	}

	private static createDateTimeIsoString(date: Date | null, time: string | null): string | null {
		if (date === null || time === null) return null;

		const timeParts = time.split(':');
		if (timeParts.length < 2) return null;

		const d = new Date(date.valueOf());
		d.setHours(parseInt(timeParts[0]), parseInt(timeParts[1]), 0, 0);
		return toISODateTimeString(d);
	}

	public mounted(): void {
		this.initialize();
		this.createBreakDurationRules();
	}

	@Emit('save')
	public save(event: Event): Event {
		this.initialize();
		return event;
	}

	@Emit('cancel')
	public cancel(event: Event): Event {
		const form = this.$refs.form as HTMLFormElement;
		this.reset([this.model], form);
		this.fromTimeInternal = null;
		this.toTimeInternal = null;
		this.initialize();
		return event;
	}

	private createBreakDurationRules(): void {
		Promise.resolve().then(() => {
			this.breakDurationRules = this.model?.rules('breakDuration') ?? [];
			const breakDurationInput = this.$refs.breakDurationInput as unknown as {validate(x: boolean): boolean} | undefined;
			if (undefined !== breakDurationInput) {
				breakDurationInput.validate(true);
			}
		});
	}

	private orderChanged(orderId: string): void {
		this.loading = true;
		this.$orderService.order(orderId)
			.then((order: Order) => {
				this.minDate = order.dateFrom;
				this.maxDate = order.dateTo;
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => this.loading = false);
	}

	private orderOptionsChanged(orders: OrderListEntry[]): void {
		if (this.model.orderId === null) return;
		if (orders.find(o => o.id === this.model.orderId) === undefined) {
			this.model.orderId = null;
		}
	}

	private dateChanged(): void {
		if (this.model.orderId === null) return;
		this.createBreakDurationRules();
		this.$emit('update:orderListFilter');
	}

	// noinspection JSMethodCanBeStatic
	private allowedMinutes(min: number): boolean {
		return min % 5 === 0;
	}

	private recalculateTimeFields(): void {
		const startDate = this.model.date !== null ? new Date(this.model.date) : null;
		const endDate = this.model.date !== null ? new Date(this.model.date) : null;
		if (endDate !== null && this.isOverMidnight) {
			endDate.setDate(endDate.getDate() + 1);
		}

		this.model.fromTime = ReportCreateDialog.createDateTimeIsoString(startDate, this.fromTimeInternal);
		this.model.toTime = ReportCreateDialog.createDateTimeIsoString(endDate, this.toTimeInternal);
	}

	private initialize(): void {
		if (this.order !== null) {
			this.model.orderId = this.order.id;
		}
		if (this.user !== null) {
			this.model.userId = this.user.id;
		}
		if (this.date !== null) {
			this.model.date = this.date;
		}
		if (this.planningEntryId !== null) {
			this.model.planningEntryId = this.planningEntryId;
		}
	}

	private requestSave(event: Event): void {
		const form = this.$refs.form as HTMLFormElement;
		if (!form.validate()) return;

		this.loading = true;
		this.$reportService.create(this.model)
			.then(res => {
				if (!this.noRedirectOnSave) {
					this.$router.push({name: 'Report', params: {id: res.id}});
				}
				this.save(event);
			})
			.catch((err: AxiosError) => {
				this.errors = err.response?.data;
			})
			.finally(() => {
				this.loading = false;
			});
	}

}
